import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  Link,
  useParams,
} from 'react-router';

import {
  Icon,
  Link as ANFLink,
} from 'anf-core-react';

import AddressList from './address-list';
import DropOffLocationsList from './drop-off-locations-list';
import RadioExpansionCard from '../radio-expansion-card/radio-expansion-card';

function getPostalCodeText(postalCodeText, currentPostalCode) {
  if (!postalCodeText.includes('{0}')) {
    return (
      <>
        <span>{postalCodeText}</span>
        &nbsp;
        <span>
          {currentPostalCode}
        </span>
        <span>:</span>
      </>
    );
  }
  const parts = postalCodeText.split('{0}');
  return (
    <>
      <span>{parts[0]}</span>
      <span>
        {currentPostalCode}
      </span>
      <span>{parts[1]}</span>
    </>
  );
}

export default function ShippingMethods({
  selectedShippingMethod,
  setSelectedShippingMethod,
  carrier,
  currentPostalCode,
}) {
  const {
    storeId,
    orderId,
  } = useParams();

  const {
    header,
    description,
    descriptionEmphasis,
    subText,
    type,
    children,
    headerBadge,
    eligibilityNoticeText,
    distributionCenterReturnAddress: addresses,
  } = carrier;

  const descriptionText = useMemo(() => {
    if (!descriptionEmphasis) {
      return description;
    }
    return (
      <h4 data-testid={`carrier-${type}-description`} className="mt-1 drop-off-subtitle-text">{description}</h4>
    );
  }, [description, descriptionEmphasis, type]);

  return (
    <div data-testid={`carrier-${type}`} className="shipping-method-card">
      <RadioExpansionCard
        id={`exchange-location-selection-card-${type}`}
        group="location"
        header={header}
        highlight={headerBadge}
        description={descriptionText}
        subText={subText}
        isSelected={selectedShippingMethod?.type === type}
        setSelected={() => setSelectedShippingMethod(carrier)}
      >
        <div className="exchange-location-selection-view-expansion-footer">
          {children?.length && children.map((child, index) => (
            <div key={`${type}-expansion-${index}`}>
              {child.showDistributionAddress && addresses && addresses.length > 0 && (
                <div data-testid={`carrier-${type}-address-list`}>
                  <AddressList
                    addresses={addresses}
                    showDistributionAddress={child.showDistributionAddress}
                  />
                </div>
              )}

              {child.postalCode && (
                <div data-testid={`carrier-${type}-postal-code`} className="capitalize primary-color">
                  {getPostalCodeText(child.postalCode, currentPostalCode)}
                </div>
              )}

              {child.showDropoffLocations && (
                <div data-testid={`carrier-${type}-drop-off-locations-list`}>
                  <DropOffLocationsList
                    dropoffLocations={carrier?.dropoffLocationsResponse?.dropoffLocations}
                  />
                </div>
              )}

              {child.dropoffLocationsLink && (
                <div data-testid={`carrier-${type}-drop-off-locations-link`}>
                  <Link to={`/store/${storeId}/exchange/${orderId}/drop-off-locations`}>
                    {child.dropoffLocationsLink}
                  </Link>
                </div>
              )}

              {child.showMapLink && (
                <div data-testid={`carrier-${type}-map-link`} className="link-wrapper">
                  <ANFLink href={carrier.mapUrl} target="_blank">
                    {child.showMapLink}
                  </ANFLink>
                  <div className="external-link-icon" data-testid={`carrier-${type}-map-link-icon`}>
                    <Icon
                      icon="external-link-anf"
                      labelText="external-link-anf"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
          {eligibilityNoticeText && (
          <div data-testid={`carrier-${type}-drop-off-locations-list-eligibilityNoticeText`}>
            {eligibilityNoticeText}
          </div>
          )}
        </div>
      </RadioExpansionCard>
    </div>
  );
}

ShippingMethods.propTypes = {
  selectedShippingMethod: PropTypes.shape({
    returnMethodId: PropTypes.number,
    name: PropTypes.string,
    type: PropTypes.string,
    distributionCenterReturnAddress: PropTypes.arrayOf(PropTypes.string),
  }),
  setSelectedShippingMethod: PropTypes.func.isRequired,
  carrier: PropTypes.shape({
    returnMethodId: PropTypes.number,
    name: PropTypes.string,
    mapUrl: PropTypes.string,
    type: PropTypes.string,
    header: PropTypes.string,
    headerBadge: PropTypes.string,
    description: PropTypes.string,
    descriptionEmphasis: PropTypes.bool,
    subText: PropTypes.string,
    eligibilityNoticeText: PropTypes.string,
    distributionCenterReturnAddress: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.arrayOf(PropTypes.shape({
      showDistributionAddress: PropTypes.string,
      postalCode: PropTypes.string,
      showDropoffLocations: PropTypes.bool,
      dropoffLocationsLink: PropTypes.string,
      showMapLink: PropTypes.string,
    })),
    dropoffLocationsResponse: PropTypes.shape({
      dropoffLocations: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        distance: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        locationId: PropTypes.string,
        geoPosition: PropTypes.shape({
          latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        }),
        providerType: PropTypes.string,
        operatingHours: PropTypes.arrayOf(PropTypes.shape({
          dayOfWeek: PropTypes.string,
          closeHours: PropTypes.string,
          openHours: PropTypes.string,
        })),
        address: PropTypes.shape({
          city: PropTypes.string,
          postalCode: PropTypes.string,
          state: PropTypes.string,
          street: PropTypes.string,
        }),
      })),
      postalCode: PropTypes.string,
      qrcodeEnabled: PropTypes.bool,
    }),
  }).isRequired,
  currentPostalCode: PropTypes.string,
};
